import { Grid, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import ProductCodeContainer from 'src/common/components/code-container/components/product-code-container';
import { setHomePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import { ScannedDataHandler } from 'src/devices/scanner-operations';
import Keypad from 'src/keypad/components/keypad';
import { codeContainerStyles } from '../common/components/code-container/components/code-container-styles';
import barcode from '../common/media/Transactions.svg';
import { resumeTransactionStyles } from './recall-transaction-style';

interface ResumeTransactionContainerProps {
  onScan: ScannedDataHandler;
  onClose: () => void;
  onList: () => void;
  onSubmit: (barcode: string) => void;
}

interface ResumeTransactionProps extends ResumeTransactionContainerProps {
  isRecallBtnDisabled: boolean;
  inputData: string;
  resetRecall: () => void;
}

const ResumeTransaction = (props: ResumeTransactionProps): JSX.Element => {
  const classes = codeContainerStyles();
  const trainingModeClasses = trainingModeStyles();
  const resumeTransactionClasses = resumeTransactionStyles();

  const { t } = useTranslation();

  const trainingMode = useSelector((state: AppState): boolean => state.posMode.trainingMode);

  const handleOnRecall = (): void => {
    props.onSubmit(props.inputData);
    props.resetRecall();
  };

  return (
    <Grid
      item
      className={`fullHeight flexFull ${classes.outerMostContainer}`}
      data-testid='recall-transaction'
    >
      <Grid
        item
        className={`${
          trainingMode ? trainingModeClasses.trainingModeBackground : classes.background
        } fullWidth fullHeight`}
      >
        <Grid
          container
          direction='column'
          className={`fullHeight ${classes.outerFlexDesign}`}
          data-testid='recall-transaction-scanning-container'
        >
          <Grid
            item
            container
            className={classes.flexDesign}
            data-testid='recall-transaction-body-container'
          >
            <Grid item container className={classes.containerAlignment}>
              <Typography
                className={`${classes.codeContainerTitleHeader} ${classes.codeContainerSubHeaderLabel} ${resumeTransactionClasses.message}`}
                data-testid='recall-transaction-body-text3'
              >
                {t('RecallTransaction_Message')}
              </Typography>
              <Grid container justifyContent='center'>
                <Grid className={classes.iconPadding}>
                  <img src={barcode} alt='Barcode' className={classes.icon} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item container justifyContent='center' className={classes.selfAlign}>
              <BaseButton
                buttonType='white'
                onClick={props.onClose}
                data-testid='recall-transaction-cancel-button'
              >
                <Typography variant={'button'}>{t('Cancel')}</Typography>
              </BaseButton>
              <BaseButton
                className={resumeTransactionClasses.listButton}
                data-testid='recall-transaction-list-button'
                onClick={props.onList}
              >
                <span className='label'>{t('TransactionList')}</span>
              </BaseButton>
              <BaseButton
                disabled={props.isRecallBtnDisabled}
                className={resumeTransactionClasses.listButton}
                data-testid='recall-transaction-recall-button'
                onClick={handleOnRecall}
              >
                <span className='label'>{t('Recall')}</span>
              </BaseButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const ResumeTransactionContainer: React.FC<ResumeTransactionContainerProps> = (
  props,
): JSX.Element => {
  const { t } = useTranslation();

  const [isRecallDisabled, setIsRecallDisabled] = useState(true);
  const [keypadEnteredBarcode, setKeypadEnteredBarcode] = useState('');

  const keypadSubmitButton = {
    label: 'OK',
    onClick: e => {
      setKeypadEnteredBarcode(e);
      setIsRecallDisabled(false);
      props.onSubmit(e);
    },
  };
  const resetRecall = (): void => {
    setKeypadEnteredBarcode('');
    setIsRecallDisabled(true);
  };

  const handleOnKeypadInputChange = (inputValue: string): void => {
    setKeypadEnteredBarcode(inputValue);
    setIsRecallDisabled(!inputValue);
  };

  return (
    <ProductCodeContainer
      headerTitle={t('RecallTransaction')}
      onKeypadSubmit={keypadSubmitButton.onClick}
      keypadComponent={
        <Keypad
          keyboardId='recall-transaction'
          disableManualWeight
          disableQuantity
          topLabelText={''}
          inputInstructionsLabelText={t('RecallTransaction_KeypadInputInstruction')}
          submitButton={keypadSubmitButton}
          visible={true}
          disableSubmitWhenNoInput={true}
          handleOnInputChange={handleOnKeypadInputChange}
          keypadTestIdPrefix='recall-transaction-'
        />
      }
    >
      <ResumeTransaction
        {...props}
        isRecallBtnDisabled={isRecallDisabled}
        inputData={keypadEnteredBarcode}
        resetRecall={resetRecall}
      />
    </ProductCodeContainer>
  );
};

export default ResumeTransactionContainer;

export const showResumeTransaction = async (
  props: ResumeTransactionContainerProps,
): Promise<void> => {
  setHomePage(<ResumeTransactionContainer {...props} />, props.onScan, { isReplaceablePage: true });
};
